// ═══════════════════════════════════════════════════════════════════════════════
// COMPOSANT - FormulaBadge
// Badge affichant la formule + AbracaCoins avec dropdown contextuel
// Particulier: "Gérer vos formules" + "Acheter plus d'AbracadaCoin"
// Pro: "Gérer vos loyers" (3 formules) + "Acheter des crédits"
// ═══════════════════════════════════════════════════════════════════════════════

import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate, useLocation } from 'react-router-dom';
import { 
  ChevronDown, 
  Coins, 
  CreditCard, 
  Settings, 
  Crown, 
  Building2,
  Sparkles,
  User,
  Check,
  Zap
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Helper pour récupérer le token
const getAuthToken = () => {
  return localStorage.getItem('ecosystem_token') || 
         localStorage.getItem('client_token') || 
         localStorage.getItem('buyer_token');
};

// Formules Pro disponibles avec prix normal et prix fondateur
const PRO_FORMULAS = [
  { id: 'pro', name: 'Pro', price: '50€/mois', priceFounder: '25€/mois', icon: Crown, color: 'text-emerald-600' },
  { id: 'business', name: 'Business', price: '100€/mois', priceFounder: '50€/mois', icon: Building2, color: 'text-blue-600' },
  { id: 'premium', name: 'Premium', price: '150€/mois', priceFounder: '75€/mois', icon: Sparkles, color: 'text-purple-600' },
];

export default function FormulaBadge({ className = '', isPro = false, compact = false }) {
  const navigate = useNavigate();
  const location = useLocation();
  const [isOpen, setIsOpen] = useState(false);
  const [balance, setBalance] = useState(null);
  const [currentFormula, setCurrentFormula] = useState(isPro ? 'pro' : 'particulier');
  const [loading, setLoading] = useState(true);
  const dropdownRef = useRef(null);
  
  // Fermer le dropdown quand on clique ailleurs
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);
  
  // Récupérer le solde et la formule
  useEffect(() => {
    const fetchData = async () => {
      try {
        const token = getAuthToken();
        const headers = token ? { 'Authorization': `Bearer ${token}` } : {};
        
        // Récupérer balance
        const balanceRes = await fetch(`${API_URL}/api/abracacoins/balance`, { headers });
        if (balanceRes.ok) {
          const data = await balanceRes.json();
          setBalance(data);
        }
        
        // TODO: Récupérer la formule actuelle de l'utilisateur depuis l'API
        // Pour l'instant on simule
        
      } catch (err) {
        console.error('Erreur récupération données:', err);
        // Fallback démo
        setBalance({
          total_available: 4250,
          is_demo: true,
        });
      } finally {
        setLoading(false);
      }
    };
    
    fetchData();
  }, []);
  
  // Formater le nombre de coins
  const formatCoins = (num) => {
    if (!num && num !== 0) return '0';
    if (num >= 1000) {
      return `${(num / 1000).toFixed(1)}k`;
    }
    return num.toString();
  };
  
  // Label de la formule
  const getFormulaLabel = () => {
    if (!isPro) return 'Particulier';
    const formula = PRO_FORMULAS.find(f => f.id === currentFormula);
    return formula ? formula.name : 'Pro';
  };
  
  // Navigation vers gestion formules/loyers
  const handleManageFormulas = () => {
    setIsOpen(false);
    if (isPro) {
      navigate('/cockpit-pro/formules');
    } else {
      navigate('/cockpit-particulier/formules');
    }
  };
  
  // Navigation vers achat crédits
  const handleBuyCredits = () => {
    setIsOpen(false);
    if (isPro) {
      navigate('/cockpit-pro/abracacoins');
    } else {
      navigate('/cockpit-particulier/abracacoins');
    }
  };
  
  // Sélection d'une formule Pro - ouvre la page de gestion des formules
  const handleSelectFormula = (formulaId) => {
    setIsOpen(false);
    navigate('/cockpit-pro/formules');
    // TODO: API pour changer de formule
    setIsOpen(false);
  };

  return (
    <div className="relative flex items-center" ref={dropdownRef}>
      {/* Badge Principal - Design épuré sur mobile */}
      <motion.button
        onClick={() => setIsOpen(!isOpen)}
        className={`
          ${compact 
            ? 'h-9 px-3 rounded-xl bg-[#FFF7ED] hover:bg-[#FFEDD5] border border-[#F97316]/30 shadow-sm inline-flex items-center justify-center space-x-1' 
            : 'flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-gradient-to-r from-amber-50 to-yellow-50 border border-amber-200'
          }
          font-medium cursor-pointer
          transition-all duration-200 hover:scale-105
          ${className}
        `}
        whileTap={{ scale: 0.97 }}
        data-testid="formula-badge"
      >
        {/* Mobile compact : emoji + nombre parfaitement centrés */}
        {compact ? (
          <>
            <span style={{ fontSize: '14px', lineHeight: '14px', display: 'flex', alignItems: 'center' }}>🪙</span>
            <span style={{ fontSize: '12px', lineHeight: '12px', fontWeight: 700, color: '#1E3A5F' }}>
              {loading ? '0' : formatCoins(balance?.total_available)}
            </span>
          </>
        ) : (
          <>
            {/* Desktop : icône + label + séparateur + coins */}
            {isPro ? (
              <Crown className="w-4 h-4 text-amber-600" />
            ) : (
              <User className="w-4 h-4 text-amber-600" />
            )}
            <span className="font-semibold">{getFormulaLabel()}</span>
            <div className="w-px h-4 bg-amber-300" />
            <span className="text-base">🪙</span>
            <span className="font-semibold tabular-nums">
              {loading ? '...' : formatCoins(balance?.total_available)}
            </span>
            <ChevronDown 
              className={`w-4 h-4 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`} 
            />
          </>
        )}
      </motion.button>
      
      {/* Dropdown - Optimisé mobile */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: -10, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -10, scale: 0.95 }}
            transition={{ duration: 0.15 }}
            className="fixed sm:absolute left-2 right-2 sm:left-auto sm:right-0 top-14 sm:top-full sm:mt-2 sm:w-72 bg-white rounded-xl shadow-xl border border-gray-200 overflow-hidden z-50"
          >
            {/* Header du dropdown */}
            <div className="px-3 py-2.5 bg-gradient-to-r from-slate-50 to-gray-50 border-b border-gray-100">
              <div className="flex items-center justify-between">
                <span className="text-xs font-medium text-gray-600">
                  {isPro ? `Formule actuelle : ${getFormulaLabel()}` : 'Mon compte'}
                </span>
                <div className="flex items-center gap-1.5 text-amber-600">
                  <span className="text-base">🪙</span>
                  <span className="font-bold text-sm">{formatCoins(balance?.total_available)}</span>
                </div>
              </div>
            </div>
            
            {/* Contenu selon le type */}
            <div className="py-2">
              {isPro ? (
                <>
                  {/* Section Gérer vos formules */}
                  <div className="px-2 py-1">
                    <p className="text-[10px] font-semibold text-gray-500 uppercase tracking-wide mb-1.5 px-1">
                      Vos formules
                    </p>
                    <div className="space-y-1">
                      {PRO_FORMULAS.map((formula) => {
                        const Icon = formula.icon;
                        const isActive = currentFormula === formula.id;
                        return (
                          <button
                            key={formula.id}
                            onClick={() => handleSelectFormula(formula.id)}
                            className={`w-full flex items-center justify-between px-2.5 py-2 rounded-lg transition-all ${
                              isActive 
                                ? 'bg-emerald-50 border border-emerald-200' 
                                : 'hover:bg-gray-50'
                            }`}
                          >
                            <div className="flex items-center gap-2">
                              <Icon className={`w-4 h-4 ${formula.color}`} />
                              <span className={`text-sm font-medium ${isActive ? 'text-emerald-700' : 'text-gray-700'}`}>
                                {formula.name}
                              </span>
                            </div>
                            <div className="flex items-center gap-1.5">
                              <span className="text-[10px] text-gray-400 line-through">{formula.price}</span>
                              <span className="text-xs font-semibold text-emerald-600">{formula.priceFounder}</span>
                              {isActive && <Check className="w-3.5 h-3.5 text-emerald-600" />}
                            </div>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                  
                  <div className="border-t border-gray-100 my-1.5" />
                  
                  {/* Acheter des AbracadaCoin */}
                  <div className="px-2 pb-1">
                    <button
                      onClick={handleBuyCredits}
                      className="w-full flex items-center gap-2.5 px-3 py-2.5 rounded-xl bg-gradient-to-r from-amber-500 to-orange-500 shadow-md hover:shadow-lg hover:from-amber-600 hover:to-orange-600 transition-all group"
                    >
                      <div className="w-7 h-7 rounded-lg bg-white/20 flex items-center justify-center flex-shrink-0">
                        <Coins className="w-3.5 h-3.5 text-white" />
                      </div>
                      <div className="text-left">
                        <p className="text-sm font-bold text-white">Acheter des AbracadaCoin</p>
                        <p className="text-[10px] text-white/70">Déploie plus de superpouvoirs</p>
                      </div>
                    </button>
                  </div>
                </>
              ) : (
                <>
                  {/* PARTICULIER - Booste ton super assistant */}
                  <div className="px-2 py-1">
                    <button
                      onClick={handleManageFormulas}
                      className="w-full flex items-center gap-2 px-2.5 py-2 rounded-lg hover:bg-slate-50 transition-colors group"
                    >
                      <div className="w-7 h-7 rounded-lg bg-slate-100 flex items-center justify-center flex-shrink-0">
                        <Zap className="w-3.5 h-3.5 text-slate-600" />
                      </div>
                      <div className="text-left">
                        <p className="text-sm font-medium text-gray-700 group-hover:text-slate-700">Booste ton super assistant</p>
                        <p className="text-[10px] text-gray-500">Plus d'IA, plus d'action, plus de mémoire.</p>
                      </div>
                    </button>
                  </div>
                  
                  <div className="border-t border-gray-100 my-1.5" />
                  
                  {/* Acheter des AbracadaCoin */}
                  <div className="px-2 pb-1">
                    <button
                      onClick={handleBuyCredits}
                      className="w-full flex items-center gap-2.5 px-3 py-2.5 rounded-xl bg-gradient-to-r from-amber-500 to-orange-500 shadow-md hover:shadow-lg hover:from-amber-600 hover:to-orange-600 transition-all group"
                    >
                      <div className="w-7 h-7 rounded-lg bg-white/20 flex items-center justify-center flex-shrink-0">
                        <Coins className="w-3.5 h-3.5 text-white" />
                      </div>
                      <div className="text-left">
                        <p className="text-sm font-bold text-white">Acheter des AbracadaCoin</p>
                        <p className="text-[10px] text-white/70">Déploie plus de superpouvoirs</p>
                      </div>
                    </button>
                  </div>
                </>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

export { FormulaBadge };
